import { infinityCards } from "../../../@shared/game/infinityCards";
import {
  WHISPERING_FOREST_STORY_PACK_CHEST_MODE,
  WHISPERING_FOREST_STORY_PACK_REWARD_IDS,
  buildWhisperingForestStoryChestRollout,
} from "../whisperingForest/storyPackChests";
import {
  createStoryChestBodies,
  getStoryChestCollisionRect,
  getStoryChestInteractionRect,
} from "../sharedChestCollision";

export const VOID_CAVE_STORY_PACK_REWARD_IDS = Object.freeze([
  ...new Set([
    "WIeYO0PUf4dm4IbFF1eY",
    "c4QXYVwD7DpxRPt3U14Z",
    "cqwCeHDc14J5DTneywEp",
    "fBrH8ZJ1swjxmkHKpe2B",
    "tbkfNf4N2ZUKZ0o7IJbv",
    "zLZ7TJphndSOaOC5n3xK",
    "vz8A1LlDNldnwqYRQPVk",
    "PwqLBRV9fGIYG5gGOId8",
    "Fki1kjjWd2nYbaC5sXww",
    "A0yMlD4g0JPmHCIeqeb5",
    ...WHISPERING_FOREST_STORY_PACK_REWARD_IDS,
  ]),
]);

export const VOID_CAVE_STORY_PACK_REWARD_POOL = Object.freeze(
  infinityCards.filter((card) =>
    VOID_CAVE_STORY_PACK_REWARD_IDS.includes(String(card?.id || "")),
  ),
);

export const VOID_CAVE_CHEST_OPEN_RESULT_EVENT =
  "whispering-forest:chest-open-result";
export const VOID_CAVE_CHEST_REQUEST_OPEN_EVENT =
  "whispering-forest:chest-request-open";
export const VOID_CAVE_CHEST_ANIMATION_COMPLETE_EVENT =
  "whispering-forest:chest-open-animation-complete";

const FLOOR_GID = 1025;
const COLLISION_LAYER_NAMES = Object.freeze(["CaveWall", "Holes", "Rocks"]);
const MAP_EDGE_PADDING = 48;
const CAVE_GROUND_DEPTH_MIN = 900;
const CAVE_GROUND_DEPTH_RANGE = 250;
const CAVE_CHEST_COLLISION_DEBUG_DEPTH = 5000;
export const SHOW_VOID_CAVE_CHEST_COLLISION_DEBUG = false;
const voidCaveStoryChestVisits = new Map();

const normalizeSceneKey = (sceneKey = "VoidCaveScene") =>
  String(sceneKey || "VoidCaveScene").trim() || "VoidCaveScene";

const cloneStoryChest = (chest = {}) => ({
  ...chest,
  rewardCard: chest.rewardCard ? { ...chest.rewardCard } : null,
  opened: Boolean(chest.opened),
});

const getLayerData = (source, name) =>
  source?.layers?.find(
    (layer) => layer.type === "tilelayer" && layer.name === name,
  )?.data || [];

const getNavigationRects = (source) =>
  source?.layers
    ?.find(
      (layer) => layer.type === "objectgroup" && layer.name === "Navigation",
    )
    ?.objects?.map((object) => ({
      x: Number(object?.x || 0),
      y: Number(object?.y || 0),
      width: Number(object?.width || 0),
      height: Number(object?.height || 0),
    })) || [];

export const buildVoidCavePointBlockedCheck = (source = null) => {
  const width = Number(source?.width || 0);
  const height = Number(source?.height || 0);
  const tileWidth = Number(source?.tilewidth || 32);
  const tileHeight = Number(source?.tileheight || 32);
  const caveGround = getLayerData(source, "CaveGround");
  const ground = getLayerData(source, "Ground");
  const collisionLayers = COLLISION_LAYER_NAMES.map((name) =>
    getLayerData(source, name),
  );

  return ({ x, y } = {}) => {
    const tileX = Math.floor(Number(x) / tileWidth);
    const tileY = Math.floor(Number(y) / tileHeight);
    if (
      tileX < 0 ||
      tileY < 0 ||
      tileX >= width ||
      tileY >= height
    ) {
      return true;
    }

    const index = tileY * width + tileX;
    if (collisionLayers.some((layer) => Boolean(layer[index]))) return true;
    return caveGround[index] !== FLOOR_GID && !ground[index];
  };
};

export const buildVoidCaveStoryChestRollout = ({
  source,
  sceneKey = "VoidCaveScene",
  entrySpawn = null,
  reservedPoints = [],
  random = Math.random,
} = {}) => {
  const mapWidth = Number(source?.width || 0) * Number(source?.tilewidth || 32);
  const mapHeight =
    Number(source?.height || 0) * Number(source?.tileheight || 32);

  return buildWhisperingForestStoryChestRollout({
    random,
    rewardPool: VOID_CAVE_STORY_PACK_REWARD_POOL,
    bounds: {
      minX: MAP_EDGE_PADDING,
      maxX: Math.max(MAP_EDGE_PADDING, mapWidth - MAP_EDGE_PADDING),
      minY: MAP_EDGE_PADDING,
      maxY: Math.max(MAP_EDGE_PADDING, mapHeight - MAP_EDGE_PADDING),
    },
    entrySpawn,
    npcStartPoints: reservedPoints,
    excludedRects: getNavigationRects(source),
    isPointBlocked: buildVoidCavePointBlockedCheck(source),
    idPrefix: `void-cave-${String(sceneKey || "cavern").toLowerCase()}`,
  });
};

export const getOrCreateVoidCaveStoryChestVisit = (options = {}) => {
  const sceneKey = normalizeSceneKey(options.sceneKey);
  if (!voidCaveStoryChestVisits.has(sceneKey)) {
    voidCaveStoryChestVisits.set(
      sceneKey,
      buildVoidCaveStoryChestRollout({ ...options, sceneKey }).map(
        cloneStoryChest,
      ),
    );
  }

  return voidCaveStoryChestVisits.get(sceneKey).map(cloneStoryChest);
};

export const markVoidCaveStoryChestOpened = (sceneKey, chestId = "") => {
  const chest = voidCaveStoryChestVisits
    .get(normalizeSceneKey(sceneKey))
    ?.find((entry) => entry.id === chestId);
  if (!chest) return false;

  chest.opened = true;
  return true;
};

export const clearVoidCaveStoryChestVisit = (sceneKey) =>
  voidCaveStoryChestVisits.delete(normalizeSceneKey(sceneKey));

export const getVoidCaveGroundEntityDepth = (worldY = 0, mapHeight = 1) => {
  const normalizedY = Math.min(
    1,
    Math.max(0, Number(worldY || 0) / Math.max(1, Number(mapHeight || 1))),
  );
  return CAVE_GROUND_DEPTH_MIN + normalizedY * CAVE_GROUND_DEPTH_RANGE;
};

export const getVoidCaveChestCollisionRect = getStoryChestCollisionRect;
export const getVoidCaveChestInteractionRect = getStoryChestInteractionRect;
export const createVoidCaveChestBodies = createStoryChestBodies;

export const drawVoidCaveChestCollisionDebug = (
  scene,
  chest,
  spriteRect,
  {
    isOpened = false,
    showDebug = SHOW_VOID_CAVE_CHEST_COLLISION_DEBUG,
  } = {},
) => {
  if (!showDebug) {
    return { collisionDebugGraphics: null, interactionDebugGraphics: null };
  }

  const collisionRect = getVoidCaveChestCollisionRect(chest, spriteRect);
  const collisionDebugGraphics = scene.add
    .graphics()
    .setDepth(CAVE_CHEST_COLLISION_DEBUG_DEPTH);
  collisionDebugGraphics.fillStyle(0xff3355, 0.35);
  collisionDebugGraphics.fillRect(
    collisionRect.x - collisionRect.width / 2,
    collisionRect.y - collisionRect.height / 2,
    collisionRect.width,
    collisionRect.height,
  );
  collisionDebugGraphics.lineStyle(1, 0xff3355, 1);
  collisionDebugGraphics.strokeRect(
    collisionRect.x - collisionRect.width / 2,
    collisionRect.y - collisionRect.height / 2,
    collisionRect.width,
    collisionRect.height,
  );

  let interactionDebugGraphics = null;
  if (!isOpened) {
    const interactionRect = getVoidCaveChestInteractionRect(chest, spriteRect);
    interactionDebugGraphics = scene.add
      .graphics()
      .setDepth(CAVE_CHEST_COLLISION_DEBUG_DEPTH);
    interactionDebugGraphics.fillStyle(0x33ddff, 0.08);
    interactionDebugGraphics.fillRect(
      interactionRect.x - interactionRect.width / 2,
      interactionRect.y - interactionRect.height / 2,
      interactionRect.width,
      interactionRect.height,
    );
    interactionDebugGraphics.lineStyle(1, 0x33ddff, 1);
    interactionDebugGraphics.strokeRect(
      interactionRect.x - interactionRect.width / 2,
      interactionRect.y - interactionRect.height / 2,
      interactionRect.width,
      interactionRect.height,
    );
  }

  return { collisionDebugGraphics, interactionDebugGraphics };
};

export const requestVoidCaveStoryChestOpen = (scene, chestId = "") => {
  const chest = scene?.chestSprites?.get(chestId);
  if (!chest || chest.opened || scene.pendingChestOpenId) return false;

  scene.pendingChestOpenId = chestId;
  window.dispatchEvent(
    new CustomEvent(VOID_CAVE_CHEST_REQUEST_OPEN_EVENT, {
      detail: {
        chestId,
        chestMode: WHISPERING_FOREST_STORY_PACK_CHEST_MODE,
        rewardCardId: chest.rewardCard?.id || null,
        rewardCard: chest.rewardCard || null,
        rewardLocation: "Void Cave Chest",
      },
    }),
  );
  return true;
};

export const attachVoidCaveStoryChestEvents = (scene) => {
  scene.pendingChestOpenId = null;
  scene._onVoidCaveChestOpenResult = (event) => {
    const { chestId, ok } = event?.detail || {};
    if (!chestId || chestId !== scene.pendingChestOpenId) return;
    scene.pendingChestOpenId = null;
    if (ok) scene.openChest(chestId);
  };
  window.addEventListener(
    VOID_CAVE_CHEST_OPEN_RESULT_EVENT,
    scene._onVoidCaveChestOpenResult,
  );
};

export const detachVoidCaveStoryChestEvents = (scene) => {
  if (!scene?._onVoidCaveChestOpenResult) return;
  window.removeEventListener(
    VOID_CAVE_CHEST_OPEN_RESULT_EVENT,
    scene._onVoidCaveChestOpenResult,
  );
  scene._onVoidCaveChestOpenResult = null;
  scene.pendingChestOpenId = null;
};
